import { useMemo, useState, type FormEvent, type JSX } from "react";
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Inline,
  LoadingState,
  Stack,
  TextInput,
  type DataTableColumn,
} from "@genius-tracker/design-system";
import { ControlNote } from "../control/ControlPrimitives.js";
import { useLocale } from "@genius-tracker/i18n/react";

import { formatTimestampSafe } from "../../../locale-format.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { usePlatformOwner } from "../../../access/platform-owner.js";
import { ApiError, apiGet, apiSend } from "../../../lib/api.js";
import { useSession } from "../../../session/SessionProvider.js";

/**
 * A-GTCC-SUP7 — Platform Grants, the minimal grant administration page.
 *
 * Scope is exactly Frage 6 of `docs/design/GTCC_SUPPORT_MODE_INVENTORY.md`:
 * pick a Tenant, a mandatory reason, a lifetime from a short list, and a list
 * of the active grants with revoke. Deliberately NOT in scope, because Frage 1
 * and Frage 5 of the same sheet answer NEIN: entering a support session from
 * here, and any banner announcing one.
 *
 * **Why this view carries its own role check** even though `GtccShell` already
 * gates the whole cockpit on `isPlatformOwner`: the API route this page writes
 * to does the same thing for a reason worth mirroring. See
 * `services/api/src/modules/admin/platform-grants.routes.ts` — grant
 * administration checks the OWNER ROLE directly rather than going through
 * `requirePlatformOwner()`, because that helper admits an owner **or** a grant
 * holder, and a grant holder who can issue grants can keep their own access
 * alive forever. The UI check saves a round trip and states the same rule in
 * the same words; the server check stays the source of truth.
 *
 * The read side (`GET /v1/admin/platform-grants`) is the security-read endpoint
 * from L-GTCC-42. It returns EVERY grant — revoked and expired ones included —
 * so "active" is a filter this view applies, not a promise the endpoint makes.
 */

/**
 * Mirror of `PLATFORM_SURFACES` in `services/api/src/auth/platform-grants.ts`.
 * `apps/admin` cannot import from `services/api`, so the list is duplicated;
 * `services/api/src/gtcc-platform-grant-surfaces.test.ts` reads both files from
 * disk and fails when they drift apart.
 *
 * The list matters at issue time: a grant whose surface is unknown to the
 * checkpoint admits nowhere (`findAdmittingGrant` skips it), and the issue
 * route answers `surface_unknown` rather than storing it.
 */
export const GTCC_PLATFORM_SURFACES = [
  "ai",
  "customer-launch-overview",
  "data-plane",
  "devices",
  "domain-routing",
  "email-deliveries",
  "gateway",
  "mail",
  "managed-hardware",
  "migrations",
  "recent-errors",
  "sims",
  "staging",
  "support-tickets",
  "telegram",
  "tenants",
] as const;

/**
 * Mirror of `MAX_GRANT_LIFETIME_HOURS` in
 * `services/api/src/auth/platform-grants.ts` — Owner decision 2026-09-05,
 * Frage 4 of the inventory: at most one support shift per grant. Kept in sync
 * by the same drift test as the surface list.
 */
export const GTCC_MAX_GRANT_LIFETIME_HOURS = 8;

/**
 * The short list from Frage 6. Filtered against the cap so the UI can never
 * offer a lifetime the route would answer `expires_at_exceeds_max_lifetime`
 * to — lowering the cap in the API narrows this list instead of breaking it.
 */
const LIFETIME_CHOICES_HOURS = [1, 4, 8] as const;

const GRANT_LIST_LIMIT = 100;

interface TenantRowDto {
  readonly id: string;
  readonly displayName: string | null;
}

interface TenantsListResponse {
  readonly items?: readonly TenantRowDto[];
}

/** `SecurityPlatformGrantItem` as it arrives over the wire (dates as ISO strings). */
interface PlatformGrantRow {
  readonly id: string;
  readonly subject: string;
  readonly tenantId: string;
  readonly scope: string | null;
  readonly issuer?: string;
  readonly grantedAt: string;
  readonly expiresAt: string;
  readonly revokedAt: string | null;
}

interface PlatformGrantsPage {
  readonly items?: readonly PlatformGrantRow[];
  readonly total?: number;
  /** True if the table held more grants than this page shows. */
  readonly truncated?: boolean;
}

interface IssueGrantVars {
  readonly subjectSub: string;
  readonly subjectEmail: string;
  readonly tenantId: string;
  readonly surface: string;
  readonly reason: string;
  readonly lifetimeHours: number;
}

function isForbidden(error: Error): boolean {
  return error instanceof ApiError && error.status === 403;
}

/** The `{ error: "<code>" }` body every failure branch of the grant routes sends. */
function apiErrorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const body = error.body;
  if (typeof body !== "object" || body === null) return undefined;
  const code = (body as { error?: unknown }).error;
  return typeof code === "string" ? code : undefined;
}

const ISSUE_ERROR_KEYS: Readonly<Record<string, string>> = {
  invalid_request: "admin.gtcc.pgrant.issue.error.invalid_request",
  surface_unknown: "admin.gtcc.pgrant.issue.error.surface_unknown",
  expires_at_must_be_in_the_future: "admin.gtcc.pgrant.issue.error.expires_at_in_past",
  expires_at_exceeds_max_lifetime: "admin.gtcc.pgrant.issue.error.expires_at_too_far",
  grant_not_created: "admin.gtcc.pgrant.issue.error.not_created",
};

const REVOKE_ERROR_KEYS: Readonly<Record<string, string>> = {
  grant_not_found_or_already_revoked: "admin.gtcc.pgrant.revoke.error.gone",
};

function isActiveGrant(row: PlatformGrantRow, nowMs: number): boolean {
  if (row.revokedAt !== null) return false;
  const expires = Date.parse(row.expiresAt);
  return Number.isFinite(expires) && expires > nowMs;
}

const selectStyle = {
  background: "var(--gt-sys-surface)",
  border: "1px solid var(--gt-sys-border)",
  borderRadius: "var(--gt-sys-radius-sm)",
  color: "var(--gt-sys-text)",
  padding: "var(--gt-sys-space-2)",
  inlineSize: "100%",
} as const;

function FieldLabel({
  children,
  text,
}: {
  readonly children: JSX.Element;
  readonly text: string;
}): JSX.Element {
  return (
    <label style={{ display: "block" }}>
      <Stack gap={1}>
        <span
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-xs)",
          }}
        >
          {text}
        </span>
        {children}
      </Stack>
    </label>
  );
}

function formatDateTime(fmt: Intl.DateTimeFormat, iso: string): string {
  return formatTimestampSafe(iso, (d) => fmt.format(d));
}

export function GtccPlatformGrantsView({
  hideHeader = false,
}: { readonly hideHeader?: boolean } = {}): JSX.Element {
  const { t, locale } = useLocale();
  const session = useSession();
  const queryClient = useQueryClient();

  const [subjectSub, setSubjectSub] = useState("");
  const [subjectEmail, setSubjectEmail] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [surface, setSurface] = useState("");
  const [reason, setReason] = useState("");
  const [lifetimeHours, setLifetimeHours] = useState(String(LIFETIME_CHOICES_HOURS[0]));
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);

  const isOwner = usePlatformOwner(session);

  const tenantsQuery = useQuery<TenantsListResponse, Error>({
    queryKey: ["gtcc", "pgrant", "tenants"],
    queryFn: ({ signal }) => apiGet<TenantsListResponse>("/v1/admin/tenants", undefined, signal),
    enabled: isOwner,
    staleTime: 60_000,
  });

  const grantsQuery = useQuery<PlatformGrantsPage, Error>({
    queryKey: ["gtcc", "pgrant", "grants"],
    queryFn: ({ signal }) =>
      apiGet<PlatformGrantsPage>(
        "/v1/admin/platform-grants",
        { limit: String(GRANT_LIST_LIMIT) },
        signal,
      ),
    enabled: isOwner,
    staleTime: 15_000,
  });

  const issueMutation = useMutation<{ id: string }, Error, IssueGrantVars>({
    mutationFn: (vars) => {
      const expiresAt = new Date(Date.now() + vars.lifetimeHours * 60 * 60 * 1000).toISOString();
      const body: Record<string, unknown> = {
        subjectSub: vars.subjectSub,
        tenantId: vars.tenantId,
        surface: vars.surface,
        reason: vars.reason,
        expiresAt,
      };
      if (vars.subjectEmail !== "") body["subjectEmail"] = vars.subjectEmail;
      return apiSend<{ id: string }>("POST", "/v1/admin/platform-grants", body);
    },
    onSuccess: () => {
      setSubjectSub("");
      setSubjectEmail("");
      setReason("");
      void queryClient.invalidateQueries({ queryKey: ["gtcc", "pgrant", "grants"] });
    },
  });

  const revokeMutation = useMutation<{ id: string }, Error, string>({
    mutationFn: (id) => apiSend<{ id: string }>("POST", `/v1/admin/platform-grants/${id}/revoke`),
    onSettled: () => {
      setConfirmRevokeId(null);
      void queryClient.invalidateQueries({ queryKey: ["gtcc", "pgrant", "grants"] });
    },
  });

  const tenants = useMemo(() => tenantsQuery.data?.items ?? [], [tenantsQuery.data]);

  const tenantLabel = useMemo(() => {
    const byId = new Map(tenants.map((row) => [row.id, row.displayName ?? row.id]));
    return (id: string) => byId.get(id) ?? id;
  }, [tenants]);

  const activeGrants = useMemo(() => {
    const rows = grantsQuery.data?.items ?? [];
    const nowMs = Date.now();
    return rows.filter((row) => isActiveGrant(row, nowMs));
  }, [grantsQuery.data]);

  const lifetimeOptions = LIFETIME_CHOICES_HOURS.filter(
    (hours) => hours <= GTCC_MAX_GRANT_LIFETIME_HOURS,
  );

  const canSubmit =
    subjectSub.trim() !== "" &&
    tenantId !== "" &&
    surface !== "" &&
    reason.trim() !== "" &&
    !issueMutation.isPending;

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!canSubmit) return;
    issueMutation.mutate({
      subjectSub: subjectSub.trim(),
      subjectEmail: subjectEmail.trim(),
      tenantId,
      surface,
      reason: reason.trim(),
      lifetimeHours: Number(lifetimeHours),
    });
  }

  // A-GTCC-FMT1: gleicher Helfer wie in der Hardware-View — der Maschinenwert
  // bleibt im dateTime-Attribut, angezeigt wird die App-Sprache.
  const dateTime = new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" });

  const columns: ReadonlyArray<DataTableColumn<PlatformGrantRow>> = [
    {
      id: "subject",
      header: t("admin.gtcc.pgrant.col.subject"),
      cell: (row) => row.subject,
    },
    {
      id: "tenant",
      header: t("admin.gtcc.pgrant.col.tenant"),
      cell: (row) => tenantLabel(row.tenantId),
    },
    {
      id: "surface",
      header: t("admin.gtcc.pgrant.col.surface"),
      cell: (row) => row.scope ?? t("admin.gtcc.pgrant.surface_none"),
    },
    {
      id: "issuer",
      header: t("admin.gtcc.pgrant.col.issuer"),
      cell: (row) => row.issuer?.trim() || t("common.unknown"),
    },
    {
      id: "granted_at",
      header: t("admin.gtcc.pgrant.col.granted_at"),
      cell: (row) => (
        <time dateTime={row.grantedAt}>{formatDateTime(dateTime, row.grantedAt)}</time>
      ),
    },
    {
      id: "expires_at",
      header: t("admin.gtcc.pgrant.col.expires_at"),
      cell: (row) => (
        <time dateTime={row.expiresAt}>{formatDateTime(dateTime, row.expiresAt)}</time>
      ),
    },
    {
      id: "actions",
      header: t("admin.gtcc.pgrant.col.actions"),
      cell: (row) =>
        confirmRevokeId === row.id ? (
          <Inline gap={2}>
            <Button
              variant="danger"
              size="sm"
              disabled={revokeMutation.isPending}
              onClick={() => revokeMutation.mutate(row.id)}
              data-testid={`gtcc-pgrant-revoke-confirm-${row.id}`}
            >
              {t("admin.gtcc.pgrant.revoke.confirm")}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirmRevokeId(null)}>
              {t("common.cancel")}
            </Button>
          </Inline>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setConfirmRevokeId(row.id)}
            data-testid={`gtcc-pgrant-revoke-${row.id}`}
          >
            {t("admin.gtcc.pgrant.revoke.action")}
          </Button>
        ),
    },
  ];

  const header = (
    <Stack gap={1}>
      <h2 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-lg)" }}>
        {t("admin.gtcc.pgrant.title")}
      </h2>
      <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
        {t("admin.gtcc.pgrant.subtitle")}
      </p>
    </Stack>
  );

  // The same rule the issue route states, stated once more here: the ROLE
  // admits, a grant does not. A session that only holds a grant sees this.
  if (!isOwner) {
    return (
      <Stack gap={4}>
        {hideHeader ? null : header}
        <div data-testid="gtcc-pgrant-role-gate">
          <ErrorState
            severity="warn"
            title={t("admin.gtcc.pgrant.forbidden.title")}
            body={t("admin.gtcc.pgrant.forbidden.body")}
          />
        </div>
      </Stack>
    );
  }

  const listError = grantsQuery.error;
  const issueErrorCode = issueMutation.error ? apiErrorCode(issueMutation.error) : undefined;
  const revokeErrorCode = revokeMutation.error ? apiErrorCode(revokeMutation.error) : undefined;

  return (
    <Stack gap={4}>
      {hideHeader ? null : header}

      <section aria-label={t("admin.gtcc.pgrant.issue.section")}>
        <Card>
          <Stack gap={3}>
            <h3 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-md)" }}>
              {t("admin.gtcc.pgrant.issue.section")}
            </h3>

            <form onSubmit={onSubmit} data-testid="gtcc-pgrant-issue-form">
              <Stack gap={3}>
                <TextInput
                  label={t("admin.gtcc.pgrant.issue.subject_sub")}
                  hint={t("admin.gtcc.pgrant.issue.subject_sub_hint")}
                  value={subjectSub}
                  onChange={(event) => setSubjectSub(event.currentTarget.value)}
                  data-testid="gtcc-pgrant-subject-sub"
                />

                <TextInput
                  label={t("admin.gtcc.pgrant.issue.subject_email")}
                  type="email"
                  value={subjectEmail}
                  onChange={(event) => setSubjectEmail(event.currentTarget.value)}
                  data-testid="gtcc-pgrant-subject-email"
                />

                <FieldLabel text={t("admin.gtcc.pgrant.issue.tenant")}>
                  <select
                    value={tenantId}
                    onChange={(event) => setTenantId(event.currentTarget.value)}
                    style={selectStyle}
                    data-testid="gtcc-pgrant-tenant"
                  >
                    <option value="">{t("admin.gtcc.pgrant.issue.tenant_placeholder")}</option>
                    {tenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.displayName ?? tenant.id}
                      </option>
                    ))}
                  </select>
                </FieldLabel>

                <FieldLabel text={t("admin.gtcc.pgrant.issue.surface")}>
                  <select
                    value={surface}
                    onChange={(event) => setSurface(event.currentTarget.value)}
                    style={selectStyle}
                    data-testid="gtcc-pgrant-surface"
                  >
                    <option value="">{t("admin.gtcc.pgrant.issue.surface_placeholder")}</option>
                    {GTCC_PLATFORM_SURFACES.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </FieldLabel>

                <FieldLabel text={t("admin.gtcc.pgrant.issue.lifetime")}>
                  <select
                    value={lifetimeHours}
                    onChange={(event) => setLifetimeHours(event.currentTarget.value)}
                    style={selectStyle}
                    data-testid="gtcc-pgrant-lifetime"
                  >
                    {lifetimeOptions.map((hours) => (
                      <option key={hours} value={String(hours)}>
                        {t("admin.gtcc.pgrant.issue.lifetime_option", { hours })}
                      </option>
                    ))}
                  </select>
                </FieldLabel>

                <FieldLabel text={t("admin.gtcc.pgrant.issue.reason")}>
                  <textarea
                    value={reason}
                    onChange={(event) => setReason(event.currentTarget.value)}
                    rows={3}
                    style={{ ...selectStyle, resize: "vertical" }}
                    data-testid="gtcc-pgrant-reason"
                  />
                </FieldLabel>

                <p
                  style={{
                    margin: 0,
                    color: "var(--gt-sys-text-muted)",
                    fontSize: "var(--gt-sys-font-size-xs)",
                  }}
                >
                  {t("admin.gtcc.pgrant.issue.reason_hint")}
                </p>

                <Inline gap={3}>
                  <Button type="submit" variant="primary" disabled={!canSubmit}>
                    {issueMutation.isPending
                      ? t("admin.gtcc.pgrant.issue.submitting")
                      : t("admin.gtcc.pgrant.issue.submit")}
                  </Button>
                  {issueMutation.isSuccess ? (
                    <span data-testid="gtcc-pgrant-issue-success">
                      {t("admin.gtcc.pgrant.issue.success")}
                    </span>
                  ) : null}
                </Inline>

                {issueMutation.error ? (
                  <div data-testid="gtcc-pgrant-issue-error">
                    <ErrorState
                      severity={isForbidden(issueMutation.error) ? "warn" : "error"}
                      title={t("admin.gtcc.pgrant.issue.error.title")}
                      body={t(
                        isForbidden(issueMutation.error)
                          ? "admin.gtcc.pgrant.forbidden.body"
                          : (ISSUE_ERROR_KEYS[issueErrorCode ?? ""] ??
                              "admin.gtcc.pgrant.issue.error.unknown"),
                      )}
                    />
                  </div>
                ) : null}
              </Stack>
            </form>
          </Stack>
        </Card>
      </section>

      <section aria-label={t("admin.gtcc.pgrant.list.section")}>
        <Card>
          <Stack gap={3}>
            <h3 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-md)" }}>
              {t("admin.gtcc.pgrant.list.section")}
            </h3>

            {revokeMutation.error ? (
              <div data-testid="gtcc-pgrant-revoke-error">
                <ErrorState
                  severity="error"
                  title={t("admin.gtcc.pgrant.revoke.error.title")}
                  body={t(
                    REVOKE_ERROR_KEYS[revokeErrorCode ?? ""] ??
                      "admin.gtcc.pgrant.revoke.error.unknown",
                  )}
                />
              </div>
            ) : null}

            {grantsQuery.isPending ? (
              <LoadingState label={t("admin.gtcc.pgrant.loading")} />
            ) : listError ? (
              <Stack gap={2}>
                <div data-testid="gtcc-pgrant-list-error">
                  <ErrorState
                    severity={isForbidden(listError) ? "warn" : "error"}
                    title={
                      isForbidden(listError)
                        ? t("admin.gtcc.pgrant.forbidden.title")
                        : t("admin.gtcc.pgrant.error.title")
                    }
                    body={
                      isForbidden(listError)
                        ? t("admin.gtcc.pgrant.forbidden.body")
                        : t("admin.gtcc.pgrant.error.body")
                    }
                  />
                </div>
                {isForbidden(listError) ? null : (
                  <Inline gap={2}>
                    <Button variant="secondary" onClick={() => void grantsQuery.refetch()}>
                      {t("common.retry")}
                    </Button>
                  </Inline>
                )}
                {listError instanceof ApiError && listError.requestId ? (
                  <code>{t("common.error.request_id", { id: listError.requestId })}</code>
                ) : null}
              </Stack>
            ) : activeGrants.length === 0 ? (
              <div data-testid="gtcc-pgrant-empty">
                <EmptyState
                  title={t("admin.gtcc.pgrant.empty.title")}
                  body={t("admin.gtcc.pgrant.empty.body")}
                />
              </div>
            ) : (
              <DataTable<PlatformGrantRow>
                caption={t("admin.gtcc.pgrant.list.caption")}
                columns={columns}
                rows={activeGrants}
                getRowId={(row) => row.id}
                testId="gtcc-pgrant-table"
                rowTestId={(row) => `gtcc-pgrant-row-${row.id}`}
              />
            )}
            {grantsQuery.data?.truncated === true ? (
              <ControlNote testId="gtcc-pgrant-truncated">
                {t("admin.gtcc.pgrant.list.truncated")}
              </ControlNote>
            ) : null}
          </Stack>
        </Card>
      </section>

      <p
        data-testid="gtcc-pgrant-scope-note"
        style={{
          margin: 0,
          color: "var(--gt-sys-text-muted)",
          fontSize: "var(--gt-sys-font-size-xs)",
        }}
      >
        {t("admin.gtcc.pgrant.scope_note")}
      </p>
    </Stack>
  );
}
